'use client';

import { useState } from 'react';
import type { Path, UseFormReturn } from 'react-hook-form';
import type { AddressLookupResult } from '../../Forms/Registration/types';
import { applyAddressLookupResultToForm } from '../../Forms/Registration/utils/addressUtils';
import { emptyManualAddressValues } from './constants';
import type { AddressFormValues } from './types';

const ADDRESS_VALIDATION_TRIGGER_FIELDS = ['address.houseNameNumber', 'address.streetName', 'address.city', 'address.state', 'address.postcode'] as const;

type UseRegistrationAddressFormModeOptions<TFormValues extends AddressFormValues> = {
  form: UseFormReturn<TFormValues>;
  initialShowManualEntry?: boolean;
};

function useRegistrationAddressFormMode<TFormValues extends AddressFormValues>({ form, initialShowManualEntry = false }: UseRegistrationAddressFormModeOptions<TFormValues>) {
  const [showManualEntry, setShowManualEntry] = useState(initialShowManualEntry);
  const [lookupOpen, setLookupOpen] = useState(false);

  const handleAddressSelect = (address: AddressLookupResult) => {
    applyAddressLookupResultToForm(form, address);
    setShowManualEntry(true);
    setLookupOpen(false);
    void form.trigger([...ADDRESS_VALIDATION_TRIGGER_FIELDS] as Path<TFormValues>[]);
  };

  const toggleEntryMode = () => {
    setLookupOpen(false);

    const narrowedForm = form as unknown as UseFormReturn<AddressFormValues>;

    if (showManualEntry) {
      narrowedForm.setValue('address', { ...emptyManualAddressValues });
      narrowedForm.setValue('addressLookup', '');
      narrowedForm.clearErrors(['address', 'addressLookup']);
    } else {
      narrowedForm.setValue('addressLookup', '');
      narrowedForm.clearErrors(['addressLookup']);
    }

    setShowManualEntry(prev => !prev);
  };

  return {
    showManualEntry,
    lookupOpen,
    setLookupOpen,
    handleAddressSelect,
    toggleEntryMode,
  };
}

export { ADDRESS_VALIDATION_TRIGGER_FIELDS, useRegistrationAddressFormMode };
